import { FC, useContext, useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { Button, Flex, PasswordInput } from "@mantine/core";

import { SettingsForm } from "@components/SettingsForm/SettingsForm";

import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

export const ChangePassword: FC = () => {
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const { t } = useTranslation();

  const { authUser } = useContext(AuthContext)!;

  const passwordForm = useForm({
    initialValues: {
      newPassword: "",
      currentPassword: "",
      confirmPassword: "",
    },
    validate: {
      confirmPassword: (_, { newPassword, confirmPassword }) =>
        newPassword !== confirmPassword ? "Password does not match" : null,
    },
  });

  const handlePasswordFormSubmit = passwordForm.onSubmit(
    async ({ newPassword, currentPassword }) => {
      setIsUpdatingPassword(true);
      try {
        await reauthenticateWithCredential(
          authUser,
          EmailAuthProvider.credential(authUser.email || "", currentPassword)
        );
        try {
          await updatePassword(authUser, newPassword);
          passwordForm.reset();
          showNotification({
            title: "Password Changed",
            message: "Password has been changed successfully!",
          });
        } catch (ex) {
          console.debug(ex);
          passwordForm.setFieldError(
            "newPassword",
            (ex as Error).message.replaceAll(/Firebase: | \(.*\)/gi, "")
          );
        }
      } catch (ex) {
        passwordForm.setFieldError(
          "currentPassword",
          (ex as Error).message.replaceAll(/Firebase: | \(.*\)/gi, "")
        );
      }
      setIsUpdatingPassword(false);
    }
  );

  return (
    <SettingsForm
      title={t("Password")}
      description={
        <Trans
          i18nKey="Please enter your current password to <br />change your password."
          shouldUnescape={true}
          components={{ b: <b /> }}
          values={{ characters: escape("<, >, &") }}
        />
      }
      onSubmit={handlePasswordFormSubmit}
    >
      <PasswordInput
        required
        label={t("Current Password")}
        {...passwordForm.getInputProps("currentPassword")}
      />
      <PasswordInput
        required
        label={t("New Password")}
        autoComplete="new-password"
        {...passwordForm.getInputProps("newPassword")}
      />
      <PasswordInput
        required
        label={t("Confirm Password")}
        autoComplete="new-password"
        {...passwordForm.getInputProps("confirmPassword")}
      />
      <Flex justify="end" gap="md">
        <Button variant="outline" disabled={isUpdatingPassword}>
          {t("Cancel")}
        </Button>
        <Button type="submit" loading={isUpdatingPassword}>
          {t("Save Password")}
        </Button>
      </Flex>
    </SettingsForm>
  );
};
